import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Lock, Unlock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MenuItem, SubItem } from './sidebarConfig';

type SidebarChildrenSectionProps = {
  item: MenuItem & { children: SubItem[] };
  isOpen: boolean;
  hasActive: boolean;
  /** Rubrique verrouillée par code (affiche le cadenas, n'ouvre pas les liens). */
  locked: boolean;
  onToggle: () => void;
  renderLink: (child: SubItem) => ReactNode;
};

/** Rubrique du menu avec sous-liens (extraite d'AppSidebar), cadenas pour les rubriques à code. */
export function SidebarChildrenSection({
  item,
  isOpen,
  hasActive,
  locked,
  onToggle,
  renderLink,
}: SidebarChildrenSectionProps) {
  return (
    <div>
      <button
        onClick={onToggle}
        className={cn(
          'mx-2 flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-all',
          'hover:bg-sidebar-accent',
          hasActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/90',
        )}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <div className="flex items-center gap-3">
          <item.icon
            className={cn(
              'h-[18px] w-[18px] shrink-0',
              hasActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/70',
            )}
          />
          <span className="flex items-center gap-1.5">
            {item.title}
            {item.codeLocked &&
              (locked ? (
                <Lock className="h-3 w-3 text-amber-500/70" />
              ) : (
                <Unlock className="h-3 w-3 text-green-500/70" />
              ))}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-3.5 w-3.5 text-sidebar-foreground/40" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-sidebar-foreground/40" />
        )}
      </button>
      {isOpen && !locked && (
        <div className="mt-0.5 pb-1">{item.children.map((child) => renderLink(child))}</div>
      )}
    </div>
  );
}
